import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, SettingsPage, useI18nContext } from "@procore/core-react";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { SettingsNavigation } from "./navigation/settings-navigation";
import { SettingsPanel } from "./settings-panel";
import { PermissionsContainer } from "./permissions/permissions-container";
import { AccountSettingsContainer } from "./account-settings/account-settings-container";
import { DataManagementContainer } from "./data-management/data-management-container";
import { GroupsSection } from "./groups/groups-section";
import { CustomFieldsSection } from "./custom-fields/custom-fields-section";
import { TagsSection } from "./tags/tags-section";
import { StatusesSection } from "./statuses/statuses-section";
import { JobTitlesSection } from "./job-titles/job-titles-section";
import { IntegrationsContainer } from "./integrations/integrations-container";
import { CostingSection } from "./costing/costing-section";
import { AlertSettingsSection } from "./alert-settings/alert-settings-section";
import { getQueryParam, replaceQueryParam } from "@/utils/query-params";

const TAB_QUERY_PARAM = "tab";

interface SettingsSection {
   id: string;
   labelKey: string;
   /* An array so a section can be reachable via any one of several actions — Alert Settings
      hosts both the alert panels and the notification panels. */
   permissions?: AuthAction[];
}

const SECTION_RENDERERS: Partial<Record<string, () => React.ReactNode>> = {
   "account-settings": () => <AccountSettingsContainer />,
   permissions: () => <PermissionsContainer />,
   groups: () => <GroupsSection />,
   tags: () => <TagsSection />,
   statuses: () => <StatusesSection />,
   "job-titles": () => <JobTitlesSection />,
   costing: () => <CostingSection />,
   "alert-settings": () => <AlertSettingsSection />,
   "custom-fields": () => <CustomFieldsSection />,
   integrations: () => <IntegrationsContainer />,
   "data-management": () => <DataManagementContainer />,
};

const SETTINGS_SECTIONS: SettingsSection[] = [
   {
      id: "account-settings",
      labelKey: "views.company.workforce_planning.settings.account_settings.title",
      permissions: [AuthAction.VIEW_COMPANY_SETTINGS],
   },
   {
      id: "permissions",
      labelKey: "views.company.workforce_planning.settings.access_controls",
      permissions: [AuthAction.VIEW_PERMISSION_SETTINGS],
   },
   {
      id: "groups",
      labelKey: "views.company.workforce_planning.settings.groups",
      permissions: [AuthAction.VIEW_GROUP_SETTINGS],
   },
   {
      id: "job-titles",
      labelKey: "views.company.workforce_planning.settings.job_titles",
      permissions: [AuthAction.VIEW_POSITION_SETTINGS],
   },
   {
      id: "tags",
      labelKey: "views.company.workforce_planning.settings.tags",
      permissions: [AuthAction.VIEW_TAGS_SETTINGS],
   },
   {
      id: "statuses",
      labelKey: "views.company.workforce_planning.settings.statuses",
      permissions: [AuthAction.VIEW_STATUS_SETTINGS],
   },
   {
      id: "alert-settings",
      labelKey: "views.company.workforce_planning.settings.alert_settings",
      permissions: [AuthAction.VIEW_ALERTS_SETTINGS, AuthAction.VIEW_NOTIFICATIONS_SETTINGS],
   },
   {
      id: "custom-fields",
      labelKey: "views.company.workforce_planning.settings.custom_fields",
      permissions: [AuthAction.VIEW_CUSTOM_FIELDS_SETTINGS],
   },
   {
      id: "costing",
      labelKey: "views.company.workforce_planning.settings.costing",
      permissions: [AuthAction.VIEW_COSTING_SETTINGS],
   },
   {
      id: "integrations",
      labelKey: "views.company.workforce_planning.settings.integrations",
      permissions: [AuthAction.VIEW_COMPANY_SETTINGS],
   },
   {
      id: "data-management",
      labelKey: "views.company.workforce_planning.settings.data_management.title",
      permissions: [AuthAction.VIEW_COMPANY_SETTINGS],
   },
];

export const SettingsContainer = () => {
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();

   const visibleSections = useMemo(() => {
      return SETTINGS_SECTIONS.filter((section) => {
         return (
            !section.permissions || section.permissions.some((action) => checkAuthAction(action))
         );
      });
   }, [checkAuthAction]);

   // Resolve the initial tab from the `?tab=` param so a reloaded/deep-linked URL
   // lands on the right section; fall back to the first visible tab.
   const getInitialTab = (): string => {
      const tab = getQueryParam(TAB_QUERY_PARAM);
      if (tab && visibleSections.some((section) => section.id === tab)) {
         return tab;
      }
      return visibleSections[0]?.id ?? "";
   };

   const [selectedId, setSelectedId] = useState<string>(getInitialTab);

   /* Mount-only: normalize a missing or stale/invalid `tab` param to the resolved
   initial tab. Deps are intentionally empty — this reflects the first render's
   selection and must not re-run on later tab changes (handleSelectTab updates
   the URL directly). */
   useEffect(() => {
      if (selectedId && getQueryParam(TAB_QUERY_PARAM) !== selectedId) {
         replaceQueryParam(TAB_QUERY_PARAM, selectedId);
      }
   }, []);

   const handleSelectTab = (id: string) => {
      replaceQueryParam(TAB_QUERY_PARAM, id);
      setSelectedId(id);
   };

   const navSections = useMemo(() => {
      return visibleSections.map((section) => ({
         id: section.id,
         label: I18n.t(section.labelKey),
      }));
   }, [visibleSections, I18n]);

   const selectedSection = visibleSections.find((s) => s.id === selectedId);

   return (
      <SettingsPage hasNavigation={false} style={{ height: "100%" }} className="border-box">
         <SettingsPage.Main style={{ height: "100%" }}>
            <Flex padding="xl" style={{ height: "100%" }}>
               <Box paddingRight="lg" style={{ minWidth: 200 }}>
                  <SettingsNavigation
                     sections={navSections}
                     selected={selectedId}
                     onSelect={handleSelectTab}
                  />
               </Box>
               {/* A flex column so sections that opt into `height="fill"` have a
                   definite height to divide up; sections that don't are sized by
                   their content exactly as before. */}
               <Box
                  style={{
                     flex: 1,
                     minWidth: 0,
                     display: "flex",
                     flexDirection: "column",
                     height: "100%",
                  }}
               >
                  {selectedSection &&
                     (SECTION_RENDERERS[selectedSection.id]?.() ?? (
                        <SettingsPage.Card>
                           <SettingsPage.Section>
                              <SettingsPanel sectionLabel={I18n.t(selectedSection.labelKey)} />
                           </SettingsPage.Section>
                        </SettingsPage.Card>
                     ))}
               </Box>
            </Flex>
         </SettingsPage.Main>
      </SettingsPage>
   );
};
